import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { useGHLLocationNames } from '@/hooks/useGHLDashboard'
import { useModelLabProviders, useStartModelLabRun } from '@/hooks/useModelLab'
import {
  contestantKey,
  defaultContestant,
  ESTIMATE_TOKENS_PER_CALL,
  formatUsd,
  TASK_META,
  TASKS,
  type Contestant,
  type ModelLabRun,
  type Task,
} from '@/lib/model-lab'
import { ContestantEditor } from './ContestantEditor'

interface Props {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /** Pre-fill from an earlier run ("Run again with…"). */
  readonly template?: ModelLabRun | null
}

const NO_REUSE = 'none'

function toIsoStart(date: string): string | undefined {
  return date ? new Date(`${date}T00:00:00`).toISOString() : undefined
}
function toIsoEnd(date: string): string | undefined {
  return date ? new Date(`${date}T23:59:59`).toISOString() : undefined
}

export function NewRunDialog({ open, onOpenChange, template }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        {/* Mounted only while open, so each opening starts from fresh initial state. */}
        {open && (
          <NewRunForm onClose={() => onOpenChange(false)} template={template} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function NewRunForm({
  onClose,
  template,
}: {
  readonly onClose: () => void
  readonly template?: ModelLabRun | null
}) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const providersQuery = useModelLabProviders()
  const locationNames = useGHLLocationNames()
  const startRun = useStartModelLabRun()

  const [locationId, setLocationId] = useState(template?.location_id ?? '')
  const [tasks, setTasks] = useState<Task[]>(template?.tasks ?? ['analysis'])
  // null = not edited yet: default to the template's models, or one row per configured provider.
  const [editedContestants, setContestants] = useState<Contestant[] | null>(
    template?.contestants ?? null,
  )
  const [samplePerTask, setSamplePerTask] = useState(20)
  const [stratify, setStratify] = useState(true)
  const [labeledOnly, setLabeledOnly] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [reuseRunId, setReuseRunId] = useState<string>(template?.id ?? NO_REUSE)

  const providers = useMemo(
    () => providersQuery.data?.providers ?? [],
    [providersQuery.data],
  )
  const maxCalls = providersQuery.data?.maxCallsPerRun ?? 2000
  const contestants = useMemo(
    () =>
      editedContestants ??
      providers.filter((p) => p.configured).map(defaultContestant),
    [editedContestants, providers],
  )

  const locations = useMemo(
    () =>
      [...(locationNames.data ?? new Map<string, string>()).entries()].sort(
        (a, b) => a[1].localeCompare(b[1]),
      ),
    [locationNames.data],
  )

  const reusing = reuseRunId !== NO_REUSE
  const uniqueContestants = new Set(contestants.map(contestantKey)).size
  const callsUpperBound = reusing
    ? null
    : tasks.length * samplePerTask * uniqueContestants

  const roughCost = useMemo(() => {
    if (callsUpperBound === null) return null
    const perCall = contestants.map((c) => {
      const price = providers
        .find((p) => p.id === c.provider)
        ?.models.find((m) => m.id === c.model)?.price
      if (!price) return null
      return (
        (ESTIMATE_TOKENS_PER_CALL.input * price.inputPerMillion +
          ESTIMATE_TOKENS_PER_CALL.output * price.outputPerMillion) /
        1_000_000
      )
    })
    if (perCall.some((p) => p === null)) return null
    return (
      (perCall as number[]).reduce((a, b) => a + b, 0) *
      tasks.length *
      samplePerTask
    )
  }, [callsUpperBound, contestants, providers, tasks.length, samplePerTask])

  const invalidModel = contestants.some((c) => c.model.length === 0)
  const tooMany = callsUpperBound !== null && callsUpperBound > maxCalls
  const canSubmit =
    locationId &&
    tasks.length > 0 &&
    contestants.length > 0 &&
    !invalidModel &&
    !tooMany &&
    !startRun.isPending

  const toggleTask = (task: Task, checked: boolean) =>
    setTasks((prev) =>
      checked ? [...prev, task] : prev.filter((t) => t !== task),
    )

  const submit = async () => {
    try {
      const { runId } = await startRun.mutateAsync({
        locationId,
        tasks,
        contestants,
        caseFilter: {
          samplePerTask,
          stratify,
          labeledOnly,
          from: toIsoStart(from),
          to: toIsoEnd(to),
          reuseRunId: reusing ? reuseRunId : undefined,
        },
      })
      onClose()
      navigate(`/admin/model-lab/runs/${runId}`)
    } catch (error) {
      toast({
        title: 'Could not start run',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>New comparison run</DialogTitle>
        <DialogDescription>
          Replays decisions the middleware already made, using the exact
          production prompts. Nothing is sent to GHL.
        </DialogDescription>
      </DialogHeader>

      {providersQuery.isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Can't reach the Model Lab on the middleware:{' '}
            {(providersQuery.error as Error).message}. It needs{' '}
            <code>MODEL_LAB_ENABLED=true</code> and{' '}
            <code>VITE_GHL_MIDDLEWARE_URL</code>.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-5">
        <div className="space-y-1.5">
          <Label>Location</Label>
          <Select
            value={locationId}
            onValueChange={setLocationId}
            disabled={reusing}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a GHL location..." />
            </SelectTrigger>
            <SelectContent>
              {locations.map(([id, name]) => (
                <SelectItem key={id} value={id}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Decisions to test</Label>
          <div className="grid grid-cols-2 gap-2">
            {TASKS.map((task) => (
              <label
                key={task}
                className="flex items-start gap-2 rounded-md border p-2.5 text-sm"
              >
                <Checkbox
                  checked={tasks.includes(task)}
                  onCheckedChange={(checked) =>
                    toggleTask(task, checked === true)
                  }
                />
                <span>
                  <span className="font-medium">{TASK_META[task].label}</span>
                  <span className="text-muted-foreground block text-xs">
                    {TASK_META[task].description}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Models</Label>
          <ContestantEditor
            providers={providers}
            contestants={contestants}
            productionModels={providersQuery.data?.productionModels}
            onChange={setContestants}
          />
          <p className="text-muted-foreground text-xs">
            DeepSeek and GLM receive transcripts with phone numbers and emails
            masked.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Conversations</Label>
          {template && (
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={reusing}
                onCheckedChange={(checked) =>
                  setReuseRunId(checked ? template.id : NO_REUSE)
                }
              />
              Reuse the exact cases from the earlier run
            </label>
          )}
          {!reusing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Cases per decision type</Label>
                <Input
                  type="number"
                  min={1}
                  max={200}
                  value={samplePerTask}
                  onChange={(e) =>
                    setSamplePerTask(
                      Math.max(1, Math.min(200, Number(e.target.value) || 1)),
                    )
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">From</Label>
                  <Input
                    type="date"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To</Label>
                  <Input
                    type="date"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={stratify} onCheckedChange={setStratify} />
                Balance across production decisions
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={labeledOnly}
                  onCheckedChange={setLabeledOnly}
                />
                Only conversations I've already labelled
              </label>
            </div>
          )}
        </div>

        <div className="bg-muted/50 rounded-md p-3 text-sm">
          {callsUpperBound === null ? (
            <span>Calls: one per reused case per model.</span>
          ) : (
            <span>
              Up to <strong>{callsUpperBound}</strong> model calls
              {roughCost !== null && <> · roughly {formatUsd(roughCost)}</>}
              {tooMany && (
                <span className="text-destructive">
                  {' '}
                  · over the {maxCalls}-call limit
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!canSubmit}>
          {startRun.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Start run
        </Button>
      </DialogFooter>
    </>
  )
}
